import { X, SaveAll, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import AddressAutocomplete from "@/shared/components/AddressAutocomplete/AddressAutocomplete";
import { zodFormResolver } from "@/shared/schemas/zodFormResolver";
import type { AddressSuggestion } from "@/shared/services/address.service";
import { Button } from "@/shared/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/Form";
import { Input } from "@/shared/ui/Input";
import { Textarea } from "@/shared/ui/TextArea";
import { useSupplierDraft } from "../hooks/useSupplierDraft";
import { supplierSchema, type SupplierFormData } from "../schemas/supplier.schema";
import type { Supplier } from "../types/suppliers.types";

type SupplierFormProps = {
  supplier?: Supplier
  onSubmitAction: (data: SupplierFormData) => Promise<void>
  onFormChange: (hasChanges: boolean) => void
  onCancel: () => void
  isLoading: boolean
  isEditMode: boolean
};

const SupplierForm = ({
  supplier,
  onSubmitAction,
  onFormChange,
  onCancel,
  isLoading,
  isEditMode,
}: SupplierFormProps) => {
  // ----- DRAFT CONTEXT ----- //
  const {
    initializeDraft, getDraft, updateDraftField, removeDraft, getChangedFields,
  } = useSupplierDraft();
  const supplierId = supplier?.id || null;
  const existingDraft = getDraft(supplierId);
  const changedFields = getChangedFields(supplierId);

  // Initialize draft with original values for comparison (only for edit mode)
  useEffect(() => {
    if (isEditMode && supplier && (!existingDraft?.originalData || Object.keys(existingDraft.originalData).length === 0)) {
      const originalValues: Partial<SupplierFormData> = {
        name: supplier.name || "",
        siret: supplier.siret || "",
        address: supplier.address || "",
        zip_code: supplier.zip_code || "",
        city: supplier.city || "",
        phone_number: supplier.phone_number || "",
        email: supplier.email || "",
        comment: supplier.comment || "",
      };
      initializeDraft(supplierId, originalValues);
    }
  }, [isEditMode, supplier?.id]);

  const defaultValues: SupplierFormData = {
    name: (existingDraft?.formData.name as string) ?? supplier?.name ?? "",
    siret: (existingDraft?.formData.siret) ?? supplier?.siret ?? "",
    address: (existingDraft?.formData.address) ?? supplier?.address ?? "",
    zip_code: (existingDraft?.formData.zip_code) ?? supplier?.zip_code ?? "",
    city: (existingDraft?.formData.city) ?? supplier?.city ?? "",
    phone_number: (existingDraft?.formData.phone_number) ?? supplier?.phone_number ?? "",
    email: (existingDraft?.formData.email) ?? supplier?.email ?? "",
    comment: (existingDraft?.formData.comment) ?? supplier?.comment ?? "",
  };

  const form = useForm<SupplierFormData>({
    resolver: zodFormResolver(supplierSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues,
  });

  const { control, watch, setValue } = form;

  const handleAddressSelect = (address: AddressSuggestion) => {
    setValue("address", address.street, { shouldValidate: true });
    setValue("zip_code", address.postcode, { shouldValidate: true });
    setValue("city", address.city, { shouldValidate: true });
    updateDraftField(supplierId, "address", address.street);
    updateDraftField(supplierId, "zip_code", address.postcode);
    updateDraftField(supplierId, "city", address.city);
  };

  // Track field changes and update draft
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        updateDraftField(supplierId, name, value[name]);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, supplierId, updateDraftField]);

  // Notify parent of changes
  useEffect(() => {
    onFormChange(changedFields.length > 0);
  }, [changedFields.length, onFormChange]);

  const handleCancel = () => {
    removeDraft(supplierId);
    onCancel();
  };

  const onSubmit = async (data: SupplierFormData) => {
    await onSubmitAction(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-20">
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dénomination *</FormLabel>
              <FormControl>
                <Input placeholder="Nom de l'entreprise" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="siret"
          render={({ field }) => (
            <FormItem>
              <FormLabel>SIRET</FormLabel>
              <FormControl>
                <Input placeholder="Numéro SIRET" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Adresse</FormLabel>
              <FormControl>
                <AddressAutocomplete
                  value={field.value || ""}
                  onChange={field.onChange}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Adresse compl\u00e8te"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="zip_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Code postal</FormLabel>
                <FormControl>
                  <Input placeholder="75001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ville</FormLabel>
                <FormControl>
                  <Input placeholder="Paris" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Téléphone</FormLabel>
              <FormControl>
                <Input placeholder="01 23 45 67 89" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="contact@entreprise.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Commentaires</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Informations complémentaires..."
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Fixed bottom action bar */}
        <div className="absolute bottom-0 inset-x-0 p-4 bg-white border-t">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancel}
              disabled={isLoading}
            >
              <X size={16} />
              Annuler
            </Button>
            <Button
              type="submit"
              variant="action"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading
                ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {isEditMode ? "Modification..." : "Création..."}
                  </>
                )
                : (
                  <>
                    <SaveAll size={16} />
                    {isEditMode ? "Modifier" : "Créer"}
                  </>
                )}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default SupplierForm;
